import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2 } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { EmailRow } from "./EmailRow";
import { GroupedEmails, groupEmails } from "./GroupedEmails";
import { useMailboxTopics } from "./useMailboxTopics";

const PAGE = 50;

export function EmailList({ params, showTarget, emptyText, testId, onLoaded, view = "list" }) {
  const topics = useMailboxTopics();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const key = JSON.stringify(params);
  const grouped = view !== "list";

  const load = useCallback(async (append = false, offset = 0) => {
    const res = await mailboxApi.list({ ...JSON.parse(key), search: search.trim() || undefined, skip: offset, limit: grouped ? 500 : PAGE });
    setItems((prev) => (append ? [...(prev || []), ...res.data.items] : res.data.items));
    setTotal(res.data.total);
    onLoaded?.(res.data.total);
  }, [key, search, onLoaded, grouped]);

  useEffect(() => {
    const t = setTimeout(() => load().catch(() => setItems([])), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Betreff, Absender, Empfänger oder Text durchsuchen…" className="pl-9" data-testid={`${testId}-search`} />
      </div>
      {items === null ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid={`${testId}-empty`}>{search ? "Keine Treffer." : emptyText}</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{total} E-Mail{total === 1 ? "" : "s"}{grouped && total > items.length ? ` (die neuesten ${items.length} gruppiert)` : ""}</p>
          {grouped ? (
            <GroupedEmails groups={groupEmails(items, view, topics)} showTarget={showTarget} onChanged={() => load()} />
          ) : items.map((e) => <EmailRow key={e.id} email={e} showTarget={showTarget} onChanged={() => load()} />)}
          {!grouped && items.length < total && (
            <div className="flex justify-center"><Button variant="outline" onClick={() => load(true, items.length)} data-testid={`${testId}-more`}>Weitere laden</Button></div>
          )}
        </>
      )}
    </div>
  );
}

export function EntityEmailsTab({ targetType, targetId }) {
  return (
    <EmailList params={{ target_type: targetType, target_id: targetId }} testId="entity-emails"
      emptyText="Noch keine E-Mails aus dem Service-Postfach zugeordnet." />
  );
}
